<script setup lang="ts" generic="Id extends string">
import { usePreferredReducedMotion, useTimeoutFn } from '@vueuse/core'
import { GripVertical } from 'lucide-vue-next'
import type { Component } from 'vue'
import { VueDraggableNext as draggable } from 'vue-draggable-next'

import { orderedSubset } from '../lib/orderedSubset'

export interface OrderedSubsetItem<T extends string = string> {
  id: T
  label: string
  icon?: Component
}

const props = withDefaults(
  defineProps<{
    items: OrderedSubsetItem<Id>[]
    modelValue: Id[]
    order?: Id[]
    minEnabled?: number
    allowHiding?: boolean
    disabledIds?: Id[]
    disabledHint?: string
    highlightId?: Id | null
  }>(),
  {
    order: () => [],
    disabledIds: () => [],
    disabledHint: undefined,
    highlightId: null,
    minEnabled: 1,
    allowHiding: true
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: Id[]]
  'update:order': [value: Id[]]
}>()

const { t } = useI18n()

const isDisabled = (id: Id) => props.disabledIds.includes(id)

const isShown = (id: Id) => !isDisabled(id) && props.modelValue.includes(id)

const reducedMotion = usePreferredReducedMotion()
const hasEntered = ref(false)
const settledId = ref<Id | null>(null)

useTimeoutFn(() => (hasEntered.value = true), 500)

const rowRefs = new Map<Id, HTMLElement>()

const setRowRef = (id: Id, el: unknown) => {
  if (el instanceof HTMLElement) rowRefs.set(id, el)
  else rowRefs.delete(id)
}

watch(
  () => props.highlightId,
  async (id) => {
    if (!id) return
    await nextTick()
    rowRefs
      .get(id)
      ?.scrollIntoView?.({ block: 'center', behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth' })
  },
  { immediate: true }
)

const onDragStart = () => {
  settledId.value = null
}

const onDragEnd = (event: { item: HTMLElement }) => {
  settledId.value = (event.item.dataset.id as Id | undefined) ?? null
}

const fullOrder = computed<Id[]>(() =>
  orderedSubset(
    props.items.map((item) => item.id),
    props.modelValue,
    props.order
  )
)

const rows = computed({
  get: () =>
    fullOrder.value
      .map((id) => props.items.find((item) => item.id === id))
      .filter((item): item is OrderedSubsetItem<Id> => item !== undefined),
  set: (next: OrderedSubsetItem<Id>[]) => commitOrder(next.map((item) => item.id))
})

const canHide = computed(() => props.modelValue.filter((id) => !isDisabled(id)).length > props.minEnabled)

const commitOrder = (next: Id[]) => {
  emit('update:order', next)
  emit(
    'update:modelValue',
    next.filter((id) => isShown(id))
  )
}

const move = (id: Id, offset: number) => {
  const next = [...fullOrder.value]
  const index = next.indexOf(id)
  const target = index + offset

  if (index === -1 || target < 0 || target >= next.length) return

  next.splice(index, 1)
  next.splice(target, 0, id)
  commitOrder(next)
}

const setVisible = (id: Id, visible: boolean) => {
  if (!visible && !canHide.value) return

  emit('update:order', fullOrder.value)
  emit(
    'update:modelValue',
    fullOrder.value.filter((value) => (value === id ? visible : isShown(value)))
  )
}
</script>

<template>
  <draggable
    v-model="rows"
    handle=".drag-handle"
    :animation="reducedMotion === 'reduce' ? 0 : 260"
    easing="cubic-bezier(0.34, 1.56, 0.64, 1)"
    :force-fallback="true"
    fallback-class="motion-safe:animate-card-lift"
    ghost-class="drag-slot"
    class="flex flex-col gap-2"
    @start="onDragStart"
    @end="onDragEnd"
  >
    <div
      v-for="(item, index) in rows"
      :key="item.id"
      :ref="(el) => setRowRef(item.id, el)"
      :data-id="item.id"
      class="group border-border flex items-center gap-3 rounded-xl border p-3 transition-[color,background-color,box-shadow] duration-700"
      :class="[
        highlightId === item.id && 'border-violet-200 border-beam',
        isShown(item.id) ? 'bg-background shadow-surface' : 'bg-muted/50',
        isDisabled(item.id) && 'opacity-50',
        settledId === item.id ? 'motion-safe:animate-card-settle' : !hasEntered && 'motion-safe:animate-card-in'
      ]"
      :style="hasEntered ? undefined : { animationDelay: `${Math.min(index, 5) * 40}ms` }"
    >
      <span v-if="isDisabled(item.id)" class="-mx-1 size-6 shrink-0" aria-hidden="true" />
      <Button
        v-else
        variant="ghost"
        size="icon-sm"
        class="drag-handle text-foreground-disabled hover:text-foreground-secondary -mx-1 size-6 cursor-grab active:cursor-grabbing"
        :aria-label="t('pages.loyaltySupports.webapp.editor.structure.orderedList.move', { name: item.label })"
        @keydown.up.prevent="move(item.id, -1)"
        @keydown.down.prevent="move(item.id, 1)"
      >
        <GripVertical class="size-4" />
      </Button>

      <span
        v-if="item.icon"
        class="flex size-7 shrink-0 items-center justify-center rounded-lg"
        :class="isShown(item.id) ? 'bg-muted' : 'bg-background'"
      >
        <component
          :is="item.icon"
          class="size-4"
          :class="isShown(item.id) ? 'text-foreground-secondary' : 'text-foreground-disabled'"
        />
      </span>

      <AppTooltip :text="isDisabled(item.id) ? disabledHint : null">
        <span
          class="min-w-0 flex-1 truncate text-sm font-medium"
          :class="isShown(item.id) ? 'text-foreground' : 'text-foreground-secondary'"
        >
          {{ item.label }}
        </span>
      </AppTooltip>

      <BaseChip
        v-if="isDisabled(item.id)"
        :text="t('pages.loyaltySupports.webapp.editor.sections.comingSoon')"
        color="gray"
      />
      <Switch
        v-else-if="allowHiding"
        :id="`orderedSubset.${item.id}`"
        :model-value="isShown(item.id)"
        :disabled="isDisabled(item.id) || (isShown(item.id) && !canHide)"
        :aria-label="t('pages.loyaltySupports.webapp.editor.structure.orderedList.show', { name: item.label })"
        @update:model-value="setVisible(item.id, $event)"
      />
    </div>
  </draggable>
</template>

<style scoped>
.drag-slot {
  border-style: dashed;
  background: transparent;
  box-shadow: none;
}

.drag-slot > * {
  opacity: 0;
}
</style>
